import { JSX } from 'react'

import { Translation } from 'cng-common/features/translations'
import { getFormattedFinance } from 'cng-common/helpers/numbers'
import { Button } from 'core-components/library/button'
import { IColumn } from 'core-components/library/list/types'

import { MDF_ACTIVITIES_TRANSLATION_KEYS } from '../../../configs/translations'
import styles from '../MdfActivitiesInitiativesGroupsTable.module.scss'
import { IMdfInitiativeGroupRow } from '../model/types'

const KEYS = MDF_ACTIVITIES_TRANSLATION_KEYS.dashboard.initiativesGroups

interface IGetColumnsParams {
  isUsd: boolean
  expandedId: string | number | null
  onToggleExpand: (id: string | number) => void
  isAllExpanded: boolean
}

const renderName = (item: IMdfInitiativeGroupRow): JSX.Element => {
  if (item.rowType === 'noInitiative') {
    return (
      <div className={styles.nameCell}>
        <div className={styles.name}>
          <Translation text={KEYS.noInitiative} />
        </div>
        <div className={styles.subtitle}>
          <Translation
            text={KEYS.unlinkedActivitiesCount}
            replaceParams={[{ key: '{count}', value: String(item.activityCount) }]}
          />
        </div>
      </div>
    )
  }

  return (
    <div className={styles.nameCell}>
      <div className={styles.name}>{item.name}</div>
      <div className={styles.subtitle}>
        <Translation
          text={KEYS.activitiesCount}
          replaceParams={[{ key: '{count}', value: String(item.activityCount) }]}
        />
      </div>
    </div>
  )
}

const renderMoney = (value: number, isUsd: boolean): JSX.Element => (
  <>{getFormattedFinance(value, { withCurrencySign: isUsd, maximumFractionDigits: 0 })}</>
)

export const getColumns = ({
  isUsd,
  expandedId,
  onToggleExpand,
  isAllExpanded,
}: IGetColumnsParams): IColumn<IMdfInitiativeGroupRow>[] => [
  {
    field: 'name',
    title: KEYS.initiative,
    disableSort: true,
    hideSettings: true,
    titleRender: () => <Translation text={KEYS.initiative} />,
    renderFunction: (item) => (
      <div className={styles.nameRow}>
        <Button
          className={styles.expandToggle}
          iconButton={`kbase ${
            isAllExpanded || expandedId === item.id ? 'kbase-caret-down' : 'kbase-caret-right'
          }`}
          size="sm"
          variant="text"
          disabled={isAllExpanded}
          onClick={(e) => {
            e.stopPropagation()
            if (!isAllExpanded) {
              onToggleExpand(item.id)
            }
          }}
        />
        {renderName(item)}
      </div>
    ),
  },
  {
    field: 'planned',
    title: KEYS.plannedFunds,
    disableSort: true,
    hideSettings: true,
    className: styles.numericColumn,
    titleRender: () => <Translation text={KEYS.plannedFunds} />,
    renderFunction: (item) => (item.planned ? renderMoney(item.planned, isUsd) : '-'),
  },
  {
    field: 'requested',
    title: KEYS.requested,
    disableSort: true,
    hideSettings: true,
    className: styles.numericColumn,
    titleRender: () => <Translation text={KEYS.requested} />,
    renderFunction: (item) => (item.requested ? renderMoney(item.requested, isUsd) : '-'),
  },
  {
    field: 'paid',
    title: KEYS.paid,
    disableSort: true,
    hideSettings: true,
    className: styles.numericColumn,
    titleRender: () => <Translation text={KEYS.paid} />,
    renderFunction: (item) => (item.paid ? renderMoney(item.paid, isUsd) : '-'),
  },
  {
    field: 'coveragePercent',
    title: KEYS.coverage,
    disableSort: true,
    hideSettings: true,
    titleRender: () => <Translation text={KEYS.coverage} />,
    renderFunction: (item) => {
      if (item.coveragePercent === null) {
        return <span className={styles.coverageValue}>—</span>
      }

      return <span className={styles.coverageValue}>{item.coveragePercent}%</span>
    },
  },
]
